// Firestore data layer for Battery Care.
//
// Schema: top-level /batteryUnits/{id}, scoped by fields. Matches recon-app-v2
// pattern ( /vehicles/{id} ). Every doc carries tenantId + storeId + department
// per the locked schema decision (2026-05-06).
//
// Doc shape:
//   {
//     tenantId:    "sevric-internal",
//     storeId:     "bmw-cincy-north",
//     department:  "lot-tech" | "new-car",
//     stockNumber, vin, year, make, model, trim,
//     voltage:     12.6,                // last reading, number
//     voltageType: "12V" | "HV",        // 12V starting battery vs HV traction
//     lastCheckAt: Timestamp,
//     lastAction:  "Checked - OK" | "Charging" | "Charge Complete"
//                | "Battery Replaced" | "HV Check OK" | "HV Charging",
//     lastCheckBy: { uid, email, initials },
//     onLotSince:  Timestamp,           // when the unit hit the lot
//     daysOnLot:   number,              // derived, refreshed on every check-in
//     history:     [{ at, by, action, voltage, notes }, ...],
//     createdAt:   Timestamp,
//     createdBy:   { uid, email },
//   }

import {
  collection, query, where, onSnapshot,
  doc, getDoc, addDoc, updateDoc,
  serverTimestamp, Timestamp, arrayUnion,
} from "firebase/firestore";
import { db, storage } from "./firebase.js";
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";

// ---------------------------------------------------------------------------
// Subscriptions
// ---------------------------------------------------------------------------

// Subscribe to all units in the user's tenant + store.
export function subscribeUnits({ tenantId, storeId, department, includeArchived = false }, cb) {
  if (!tenantId) {
    cb([], new Error("Missing tenantId"));
    return () => {};
  }
  let q = query(
    collection(db, "batteryUnits"),
    where("tenantId", "==", tenantId),
    where("storeId",  "==", storeId || ""),
  );
  if (department) {
    q = query(q, where("department", "==", department));
  }
  return onSnapshot(q,
    (snap) => {
      let units = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      // Drop soft-archived units by default. Auto-archive happens on each DAR
      // import for vehicles that have left the lot (archiveReason="left-the-DAR").
      // Pass includeArchived: true to see them again (future "departed" tab).
      if (!includeArchived) {
        units = units.filter((u) => !u.archivedAt);
      }
      cb(units, null);
    },
    (err) => cb(null, err),
  );
}

// Subscribe to a single unit doc.
// `guard` (optional) is defense-in-depth tenant/store scoping: pass
// { tenantId, storeAccess } and a doc from another tenant/store is treated as
// not-found even if Firestore rules ever regress. Firestore rules remain the
// real boundary; this stops a guessed/enumerated id from rendering foreign data.
export function subscribeUnit(id, cb, guard = null) {
  return onSnapshot(doc(db, "batteryUnits", id),
    (snap) => {
      if (!snap.exists()) { cb(null, new Error("not_found")); return; }
      const data = snap.data();
      if (guard) {
        if (guard.tenantId && data.tenantId !== guard.tenantId) {
          cb(null, new Error("not_found")); return;
        }
        if (Array.isArray(guard.storeAccess) && guard.storeAccess.length > 0 &&
            data.storeId && !guard.storeAccess.includes(data.storeId)) {
          cb(null, new Error("not_found")); return;
        }
      }
      cb({ id: snap.id, ...data }, null);
    },
    (err) => cb(null, err),
  );
}

// Subscribe to the store doc — reads tenantId, lat/lng, zip, brand overrides.
// Used by TV mode to fetch weather for the rooftop's actual location.
// Doc shape (set by admin, optional fields):
//   { tenantId, name, lat?, lng?, zip?, city?, state?, timezone? }
export function subscribeStore(storeId, cb) {
  if (!storeId) { cb(null, new Error("Missing storeId")); return () => {}; }
  return onSnapshot(doc(db, "stores", storeId),
    (snap) => {
      if (!snap.exists()) { cb(null, new Error("store_not_found")); return; }
      cb({ id: snap.id, ...snap.data() }, null);
    },
    (err) => cb(null, err),
  );
}

// ---------------------------------------------------------------------------
// Action vocabulary
// ---------------------------------------------------------------------------

export const ACTIONS = [
  "Checked - OK",
  "Charging",
  "Charge Complete",
  "Battery Replaced",
  "HV Check OK",
  "HV Charging",
];

// Per-card quick-action buttons. Three primaries + the catch-all "More" that
// opens the full form. The primary button picks itself based on whether the
// unit is HV — HV units swap "OK" for "HV Check OK" since that's the action
// a tech actually records when they confirm a hybrid is fine.
export function quickActionsFor(unit) {
  if (unit?.voltageType === "HV") {
    return {
      primary:    { label: "HV Check OK",  action: "HV Check OK" },
      secondary:  { label: "HV Charging",  action: "HV Charging" },
      tertiary:   { label: "Replace",      action: "Battery Replaced" },
    };
  }
  return {
    primary:    { label: "OK",       action: "Checked - OK" },
    secondary:  { label: "Charging", action: "Charging" },
    tertiary:   { label: "Replace",  action: "Battery Replaced" },
  };
}

// ---------------------------------------------------------------------------
// Writes
// ---------------------------------------------------------------------------

// Build the lastCheckBy / history-row author shape from a profile.
//
// We snapshot the user's displayName into the write so widgets render the
// real name forever — even if the user later signs out, their email
// changes, or rules block reading their user doc. Initials prefer the
// displayName too ("Andrew Monahan" → "AM"), falling back to the email
// local-part only if no real name is set.
export function authorFromProfile(profile) {
  if (!profile) return { uid: null, email: null, displayName: null, initials: "—" };
  const local = (profile.email || "").split("@")[0] || "";
  const friendly = profile.displayName || profile.name || local;
  const parts = friendly.split(/[.\-_\s]+/).filter(Boolean);
  const initials = parts.length >= 2
    ? (parts[0][0] + parts[1][0]).toUpperCase()
    : (local.slice(0, 2) || "—").toUpperCase();
  return {
    uid:         profile.uid,
    email:       profile.email,
    displayName: profile.displayName || null,
    initials,
  };
}

// Bulk import — used by the DAR PDF parser. Takes an array of unit-shaped
// payloads (already mapped via darEntryToUnit) and writes them via Firestore
// transactions, deduplicating on (tenantId, storeId, stockNumber). Returns
// { created, updated, skipped, errors[] }.
//
// Update behavior (locked 2026-05-07): a DAR import is the canonical truth
// for the lot. Re-importing the same DAR resets every unit to a "never
// charged" baseline — voltage cleared, voltageType back to 12V, lastCheck*
// nulled, history wiped. The DAR is the system of record for what's on
// the lot today; if we've imported it, we want a clean slate. Tech-recorded
// state lives forward-only after the most recent import.
//
// Pass `ctx.preserveHistory: true` to opt back into the legacy merge-only
// behavior (used by automated re-imports that should NOT wipe charge state).
export async function bulkImportUnits(units, ctx) {
  if (!Array.isArray(units) || units.length === 0) {
    return { created: 0, updated: 0, skipped: 0, errors: [] };
  }
  if (!ctx?.tenantId || !ctx?.storeId) {
    throw new Error("bulkImportUnits: ctx.tenantId and ctx.storeId required");
  }

  let created = 0, updated = 0, skipped = 0;
  const errors = [];
  const now = serverTimestamp();
  const Timestamp = (await import("firebase/firestore")).Timestamp;
  const preserveHistory = !!ctx.preserveHistory;

  for (const u of units) {
    if (!u.stockNumber) { skipped++; continue; }
    try {
      // Find existing unit by (tenantId, storeId, stockNumber).
      const q = query(
        collection(db, "batteryUnits"),
        where("tenantId",    "==", ctx.tenantId),
        where("storeId",     "==", ctx.storeId),
        where("stockNumber", "==", u.stockNumber),
      );
      const snap = await (await import("firebase/firestore")).getDocs(q);

      if (!snap.empty) {
        // Update the existing doc.
        const existing = snap.docs[0];
        const onLotSinceTs = u.onLotSince
          ? Timestamp.fromDate(u.onLotSince)
          : existing.data().onLotSince || null;
        const baseUpdate = {
          model:      u.model      || existing.data().model,
          chassis:    u.chassis    || existing.data().chassis,
          daysOnLot:  u.daysOnLot,
          onLotSince: onLotSinceTs,
          sold:       u.sold,
          customer:   u.customer   || existing.data().customer || "",
          available:  u.available  || existing.data().available || "",
          updatedAt:  now,
        };
        // Default: a DAR import resets the charge baseline. Voltage, history,
        // lastCheck*, AND charging-state all go back to "never checked" so
        // the lot tech sees every imported unit as fresh and re-walks them.
        // Sticky note + lot position survive — they're tech-managed metadata
        // about the physical car, not its charge state.
        const resetFields = preserveHistory ? {} : {
          voltage:           null,
          voltageType:       "12V",
          lastCheckAt:       null,
          lastAction:        null,
          lastCheckBy:       null,
          history:           [],
          charging:          false,
          chargingStartedAt: null,
        };
        await updateDoc(doc(db, "batteryUnits", existing.id), {
          ...baseUpdate,
          ...resetFields,
        });
        updated++;
        continue;
      }

      // Create new doc.
      const onLotSinceTs = u.onLotSince ? Timestamp.fromDate(u.onLotSince) : now;
      await addDoc(collection(db, "batteryUnits"), {
        tenantId:          u.tenantId,
        storeId:           u.storeId,
        department:        u.department,
        stockNumber:       u.stockNumber,
        vin:               u.vin     || "",
        chassis:           u.chassis || "",
        year:              u.year,
        make:              u.make,
        model:             u.model,
        trim:              u.trim    || "",
        voltage:           u.voltage,
        voltageType:       u.voltageType,
        lastCheckAt:       u.lastCheckAt,
        lastAction:        u.lastAction,
        lastCheckBy:       u.lastCheckBy,
        onLotSince:        onLotSinceTs,
        daysOnLot:         u.daysOnLot,
        history:           u.history || [],
        sold:              !!u.sold,
        customer:          u.customer  || "",
        available:         u.available || "",
        // Legacy v8 parity fields — explicit defaults so reads don't have
        // to coalesce undefined.
        charging:          false,
        chargingStartedAt: null,
        note:              "",
        lotPos:            "",
        completed:         false,
        createdAt:         now,
        createdBy:         ctx.by || null,
      });
      created++;
    } catch (err) {
      errors.push({ stockNumber: u.stockNumber, error: err.message || String(err) });
    }
  }

  return { created, updated, skipped, errors };
}

// =============================================================================
// archiveAbsentUnits — auto-archive units that have dropped off the DAR.
// -----------------------------------------------------------------------------
// Called immediately after bulkImportUnits. Compares the set of stock numbers
// in the freshly-imported DAR against every active unit in /batteryUnits for
// the same (tenantId, storeId, department). Any unit whose stockNumber is NOT
// in the new DAR — and isn't already archived — gets a soft-archive write:
//
//   archivedAt:     serverTimestamp()        (new field)
//   archiveReason:  "left-the-DAR"           (new field)
//   sold:           true                     (assume sold — left the lot)
//   updatedAt:      serverTimestamp()
//
// We never DELETE — the row stays in Firestore for historical reporting,
// it just stops showing up in Today / Walk / Inventory queries that filter
// on `archivedAt == null`.
//
// Returns { archived, errors }.
//
// IMPORTANT: pass the EXACT stock-number set from the current DAR import.
// Don't merge across multiple imports — we use this set as the source of
// truth for "what's on the lot right now".
// =============================================================================
export async function archiveAbsentUnits(currentStockNumbers, ctx) {
  if (!ctx?.tenantId || !ctx?.storeId) {
    throw new Error("archiveAbsentUnits: ctx.tenantId and ctx.storeId required");
  }
  const present = new Set(
    Array.isArray(currentStockNumbers)
      ? currentStockNumbers.filter(Boolean).map((s) => String(s).trim())
      : []
  );
  // Guardrail: refuse to reconcile against an empty present-set. That almost
  // always means the user uploaded an empty/bad PDF, and we'd mass-archive
  // the entire lot. Bail.
  if (present.size === 0) {
    return { archived: 0, errors: [], skipped: "no current stock numbers — skipped" };
  }

  const department = ctx.department || "new-car";
  const { getDocs, writeBatch } = await import("firebase/firestore");
  const now = serverTimestamp();

  // Pull every active (non-archived) unit for this store/department.
  const q = query(
    collection(db, "batteryUnits"),
    where("tenantId",   "==", ctx.tenantId),
    where("storeId",    "==", ctx.storeId),
    where("department", "==", department),
  );
  const snap = await getDocs(q);

  let archived = 0;
  const errors = [];

  // Write in chunks of 400 to stay under Firestore's 500-op batch cap.
  let batch = writeBatch(db);
  let opsInBatch = 0;
  const commits = [];

  for (const d of snap.docs) {
    const data = d.data();
    // Skip units already archived (idempotent re-imports).
    if (data.archivedAt) continue;
    // Skip units that ARE in the new DAR.
    const sn = (data.stockNumber || "").trim();
    if (!sn || present.has(sn)) continue;

    batch.update(d.ref, {
      archivedAt:    now,
      archiveReason: "left-the-DAR",
      sold:          true,
      updatedAt:     now,
    });
    archived++;
    opsInBatch++;
    if (opsInBatch >= 400) {
      commits.push(batch.commit());
      batch = writeBatch(db);
      opsInBatch = 0;
    }
  }
  if (opsInBatch > 0) commits.push(batch.commit());

  try {
    await Promise.all(commits);
  } catch (err) {
    errors.push({ phase: "batch-commit", error: err.message || String(err) });
  }

  return { archived, errors };
}

// Create a new unit. Used for initial seeding and the future "Add unit" form.
export async function createUnit({
  tenantId, storeId, department,
  stockNumber, vin = "", year, make, model, trim = "",
  voltage = null, voltageType = "12V",
  by,
}) {
  if (!tenantId || !storeId || !department || !stockNumber) {
    throw new Error("createUnit: tenantId, storeId, department, stockNumber required");
  }
  const now = serverTimestamp();
  const payload = {
    tenantId, storeId, department,
    stockNumber, vin, year, make, model, trim,
    voltage, voltageType,
    lastCheckAt: voltage != null ? now : null,
    lastAction:  voltage != null ? "Checked - OK" : null,
    lastCheckBy: voltage != null ? by : null,
    onLotSince:  now,
    daysOnLot:   0,
    history:     [],
    createdAt:   now,
    createdBy:   by,
  };
  const ref = await addDoc(collection(db, "batteryUnits"), payload);
  return ref.id;
}

// Helper that does the actual write. Used by both recordCheckIn (form path)
// and recordQuickCheck (one-tap path).
//
// Charging-state semantics, ported from the legacy v8 app (line 1773):
//   action === "Charging" or "HV Charging" → charging=true,  chargingStartedAt=now
//   any other action                       → charging=false, chargingStartedAt=null
// The "charging" state overrides cycle status (overdue/due) until the tech
// taps "Done Charging" (= records "Charge Complete"), at which point the
// charge cycle resets.
async function applyCheckIn(unitId, { voltage, action, notes = "", by, isQuick = false }) {
  if (!unitId) throw new Error("applyCheckIn: unitId required");
  if (!ACTIONS.includes(action)) {
    throw new Error(`applyCheckIn: action must be one of ${ACTIONS.join(" | ")}`);
  }
  const ref = doc(db, "batteryUnits", unitId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error("applyCheckIn: unit not found");
  const data = snap.data();

  // Quick check carries forward the last known voltage. Form check requires
  // a fresh number.
  const v = isQuick
    ? (typeof data.voltage === "number" ? data.voltage : null)
    : voltage;
  if (!isQuick && (typeof v !== "number" || Number.isNaN(v))) {
    throw new Error("applyCheckIn: voltage must be a number");
  }

  const onLotSince = data.onLotSince?.toDate ? data.onLotSince.toDate() : null;
  const days = onLotSince
    ? Math.max(0, Math.floor((Date.now() - onLotSince.getTime()) / 86400000))
    : (data.daysOnLot || 0);

  const isHvAction = action === "HV Check OK" || action === "HV Charging";
  const isChargingAction = action === "Charging" || action === "HV Charging";

  await updateDoc(ref, {
    voltage:           v,
    voltageType:       isHvAction ? "HV" : (data.voltageType || "12V"),
    lastCheckAt:       serverTimestamp(),
    lastAction:        action,
    lastCheckBy:       by,
    daysOnLot:         days,
    // Charging-state tracking (legacy parity).
    charging:          isChargingAction,
    chargingStartedAt: isChargingAction ? serverTimestamp() : null,
    history:           arrayUnion({
      at:      Timestamp.now(),
      by:      by || null,
      action,
      voltage: v,
      notes:   notes || "",
      kind:    isQuick ? "quick" : "form",
    }),
  });
}

// Per-unit sticky note. Separate from history — this is the "Customer
// picking up at 3pm / Keys with finance / Do NOT move" persistent text
// field from the legacy v8 app. Empty string clears the note.
export async function setStickyNote(unitId, note) {
  if (!unitId) throw new Error("setStickyNote: unitId required");
  await updateDoc(doc(db, "batteryUnits", unitId), {
    note: typeof note === "string" ? note : "",
  });
}

// Per-unit lot position (text). Same legacy field as v.lotPos.
export async function setLotPosition(unitId, lotPos) {
  if (!unitId) throw new Error("setLotPosition: unitId required");
  await updateDoc(doc(db, "batteryUnits", unitId), {
    lotPos: typeof lotPos === "string" ? lotPos : "",
  });
}

// Update a store's configuration fields. Manager-only writes — Firestore
// rules enforce the role check. Allowed fields: name, zip, city, state,
// lat, lng, dueDays, overdueDays, damageDays, logoUrl, logoMonoUrl,
// timezone. Anything else in the patch is silently dropped client-side
// so a malformed call doesn't write garbage to the doc.
const STORE_WRITABLE = new Set([
  "name", "zip", "city", "state", "lat", "lng",
  "dueDays", "overdueDays", "damageDays",
  "logoUrl", "logoMonoUrl", "timezone",
]);
export async function updateStoreConfig(storeId, patch) {
  if (!storeId) throw new Error("updateStoreConfig: storeId required");
  if (!patch || typeof patch !== "object") return;
  const clean = {};
  for (const [k, v] of Object.entries(patch)) {
    if (!STORE_WRITABLE.has(k)) continue;
    if (v === undefined) continue;
    // Coerce day-count threshold inputs to integers; strip non-finite.
    if (k === "dueDays" || k === "overdueDays" || k === "damageDays") {
      const n = parseInt(v, 10);
      clean[k] = Number.isFinite(n) ? n : null;
      continue;
    }
    if (k === "lat" || k === "lng") {
      const n = parseFloat(v);
      clean[k] = Number.isFinite(n) ? n : null;
      continue;
    }
    clean[k] = v;
  }
  if (Object.keys(clean).length === 0) return;
  await updateDoc(doc(db, "stores", storeId), clean);
}

// Upload a store logo to Cloud Storage and stamp the resulting download
// URL onto the store doc as `logoUrl`. Path: /stores/{storeId}/logo.<ext>
// — predictable so re-uploading replaces the previous file cleanly. Pass
// `mono: true` to write to `logoMonoUrl` instead (the print-only variant
// the weekly report can prefer over the color version).
export async function setStoreLogo(storeId, file, { mono = false } = {}) {
  if (!storeId) throw new Error("setStoreLogo: storeId required");
  if (!file)    throw new Error("setStoreLogo: file required");
  const ext = (file.name?.split(".").pop() || "png").toLowerCase().slice(0, 6);
  const fieldName = mono ? "logoMonoUrl" : "logoUrl";
  const objectPath = `stores/${storeId}/${mono ? "logo-mono" : "logo"}.${ext}`;
  const ref = storageRef(storage, objectPath);
  await uploadBytes(ref, file, { contentType: file.type || "image/png" });
  const url = await getDownloadURL(ref);
  await updateDoc(doc(db, "stores", storeId), { [fieldName]: url });
  return url;
}

// Clear a store logo — removes the field from the store doc AND attempts
// to delete the underlying Storage object (best-effort; we ignore the
// 404 case where the file was already gone).
export async function clearStoreLogo(storeId, { mono = false } = {}) {
  if (!storeId) throw new Error("clearStoreLogo: storeId required");
  const fieldName = mono ? "logoMonoUrl" : "logoUrl";
  await updateDoc(doc(db, "stores", storeId), { [fieldName]: null });
  // Try every common extension since we don't know which was used.
  const exts = ["png", "jpg", "jpeg", "svg", "webp", "gif"];
  await Promise.allSettled(exts.map((e) =>
    deleteObject(storageRef(storage, `stores/${storeId}/${mono ? "logo-mono" : "logo"}.${e}`)),
  ));
}

// Flip the sold flag manually (e.g. a manager retires a unit before the
// next DAR re-import would auto-mark it sold). Sold units drop out of
// every queue. Passing `false` reactivates a unit that was prematurely
// marked sold — useful when a deal falls through.
export async function setSoldFlag(unitId, sold) {
  if (!unitId) throw new Error("setSoldFlag: unitId required");
  await updateDoc(doc(db, "batteryUnits", unitId), {
    sold: !!sold,
  });
}

// Subscribe to the trailing N days of compliance snapshots for a single
// (tenant, store). Returned in chronological order (oldest → newest) so the
// sparkline renders left-to-right naturally. The collection is written by
// the batterySnapshotDaily Cloud Function. Empty until the function has
// run at least once.
export function subscribeRecentSnapshots({ tenantId, storeId, days = 28 }, cb) {
  if (!tenantId || !storeId) {
    cb([], new Error("Missing tenantId/storeId"));
    return () => {};
  }
  // Build a list of YYYY-MM-DD strings for the last N days so we can do a
  // simple `where doc id in [...]` query. Firestore caps `in` at 30 — we
  // chunk if needed. For the 28-day default this is one query.
  const today = new Date();
  const days0 = Math.max(1, Math.min(days, 90));
  const ids = [];
  for (let i = 0; i < days0; i++) {
    const d = new Date(today.getFullYear(), today.getMonth(), today.getDate() - i);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    ids.push(`${tenantId}_${storeId}_${yyyy}-${mm}-${dd}`);
  }
  // For >30 we'd chunk, but stay simple at the call site for now.
  // Firestore SDK does not expose a single-call `in` against doc ids
  // efficiently for arbitrary lists — read each via getDoc and feed back.
  // Cheap because the collection is sparse (one doc per day per store).
  let cancelled = false;
  (async () => {
    try {
      const snaps = await Promise.all(ids.map((id) => getDoc(doc(db, "batterySnapshots", id))));
      if (cancelled) return;
      const rows = [];
      for (const s of snaps) {
        if (s.exists()) rows.push({ id: s.id, ...s.data() });
      }
      // Sort ascending by date string (lexically OK for YYYY-MM-DD).
      rows.sort((a, b) => (a.date || "").localeCompare(b.date || ""));
      cb(rows, null);
    } catch (err) {
      if (!cancelled) cb(null, err);
    }
  })();
  return () => { cancelled = true; };
}

// The 20% path: action + optional voltage + optional notes from the form.
// Voltage is optional now (post 2026-05-07 time-based pivot). When omitted,
// behaves like a quick check — the action gets recorded and the cycle is
// updated, but no new voltage reading is written. Pass a number to record
// a fresh reading.
export async function recordCheckIn({ unitId, voltage, action, notes = "", by }) {
  const hasVoltage = typeof voltage === "number" && !Number.isNaN(voltage);
  return applyCheckIn(unitId, {
    voltage: hasVoltage ? voltage : null,
    action,
    notes,
    by,
    isQuick: !hasVoltage,
  });
}

// The 80% path: one tap on a card. Records the action, carries forward the
// last known voltage, advances time. Use for "I walked over, looked, fine."
export async function recordQuickCheck({ unitId, action, by }) {
  return applyCheckIn(unitId, { action, by, isQuick: true });
}

// ---------------------------------------------------------------------------
// Derived state (pure helpers — no Firestore reads)
// ---------------------------------------------------------------------------

// Voltage state classification for 12V automotive batteries. Thresholds
// match the legacy BatteryCareTracker_v8_FINAL_DEPLOY.html app:
//   < 12.0      → critical
//   12.0 – 12.5 → warning
//   >= 12.5     → ok
//   HV unit     → hv (separate state, not threshold-classified)
//   null        → unknown
export function voltageState(unit) {
  if (!unit) return "unknown";
  if (unit.voltageType === "HV") return "hv";
  const v = unit.voltage;
  if (typeof v !== "number") return "unknown";
  if (v < 12.0) return "critical";
  if (v < 12.5) return "warning";
  return "ok";
}

// Charge-cycle thresholds (days). Match the legacy app's Settings panel:
// Overdue Threshold (days) defaults to 35, "due" window is 28+ days.
// We expose them as constants so the manager view + lot walk can render
// a single source of truth.
export const CHARGE_CYCLE = {
  dueDays:     28, // 28+ days → "due this week"
  overdueDays: 35, // 35+ days → "overdue" (battery damage risk after 45)
  damageDays:  45, // 45+ days → permanent battery damage risk
};

// Per-store override. Updated by `applyStoreCycleOverrides` whenever a
// fresh /stores/{storeId} doc lands. Everything internal that needs to
// know the current effective threshold (chargeCycleStatus / needsCharge
// / isCheckStale) reads through `effectiveCycle()` instead of touching
// CHARGE_CYCLE directly. Module-level mutable state is deliberate — the
// cycle is global per-(tenant, store) and rarely changes (maybe once a
// quarter), so threading it through every component would add far more
// noise than it's worth.
let _activeCycle = { ...CHARGE_CYCLE };
export function effectiveCycle() { return _activeCycle; }

// Per-store app config — what the portal Settings page writes to
// /stores/{storeId}.apps.battery.{enabled, features, config}.
// Default to "enabled with all features on" so an empty/legacy store doc
// still works correctly while the SEVRIC admin gets around to setting it.
const APP_DEFAULTS = {
  enabled: true,
  features: { tvMode: true, weeklyReport: true },
};
let _batteryAppConfig = { ...APP_DEFAULTS, features: { ...APP_DEFAULTS.features } };
export function batteryAppConfig() { return _batteryAppConfig; }

// Subscribe to module-level config changes — used by React via the
// useBatteryAppConfig() hook below. Plain event-emitter so we don't pull
// React state through every page.
const _appConfigListeners = new Set();
export function subscribeBatteryAppConfig(cb) {
  _appConfigListeners.add(cb);
  return () => _appConfigListeners.delete(cb);
}
function emitAppConfig() {
  for (const l of _appConfigListeners) { try { l(); } catch {} }
}

// Apply a full store doc — pushes cycle overrides AND per-app config into
// the module-level state. Layout calls this whenever the store doc lands.
export function applyStoreCycleOverrides(store) {
  // Charge cycle thresholds — accept both top-level (legacy) and
  // apps.battery.config (current) shapes.
  const next = { ...CHARGE_CYCLE };
  const batt = (store?.apps?.battery) || {};
  const cfg  = batt.config || {};
  const pick = (a, b) => Number.isFinite(a) ? a : (Number.isFinite(b) ? b : null);
  const due  = pick(cfg.dueDays,     store?.dueDays);
  const od   = pick(cfg.overdueDays, store?.overdueDays);
  const dmg  = pick(cfg.damageDays,  store?.damageDays);
  if (due !== null) next.dueDays     = due;
  if (od  !== null) next.overdueDays = od;
  if (dmg !== null) next.damageDays  = dmg;
  _activeCycle = next;

  // Per-app feature flags. Master is treated as ON by default if the field
  // is missing — we only flip it OFF when the SEVRIC admin explicitly
  // disables it via the setStoreAppEnabled callable.
  const newCfg = {
    enabled:  batt.enabled !== false,
    features: {
      tvMode:       batt.features?.tvMode       !== false,
      weeklyReport: batt.features?.weeklyReport !== false,
    },
  };
  // Only emit if anything actually changed (cheap deep compare).
  const changed = JSON.stringify(newCfg) !== JSON.stringify(_batteryAppConfig);
  _batteryAppConfig = newCfg;
  if (changed) emitAppConfig();
}

// "Needs charge" status — what the legacy app rendered as the per-vehicle
// status pill. Driven by days since last check, or days on lot if the
// vehicle has never been checked. Mirrors v8 line 1107:
//   sold       → sold        (terminal)
//   charging   → charging    (in progress; overrides due/overdue)
//   never chk + daysOnLot > 35  → overdue
//   never chk + daysOnLot >= 28 → due
//   never chk                   → new
//   checked   + dsCheck   > 35  → overdue
//   checked   + dsCheck   >= 28 → due
//   checked                     → ok
export function chargeCycleStatus(unit) {
  if (!unit) return "new";
  if (unit.sold)     return "sold";
  if (unit.charging) return "charging";
  const cyc = _activeCycle;
  const days = daysSinceCheck(unit);
  if (days == null) {
    // Never checked — fall back to days on lot.
    const dol = unit.daysOnLot ?? 0;
    if (dol > cyc.overdueDays) return "overdue";
    if (dol >= cyc.dueDays)    return "due";
    return "new";
  }
  if (days > cyc.overdueDays) return "overdue";
  if (days >= cyc.dueDays)    return "due";
  return "ok";
}

// Elapsed time since charging started, formatted like the legacy app
// ("2h 14m" / "47m"). Returns "" if not currently charging or no start
// timestamp on record.
export function elapsedCharging(unit) {
  if (!unit?.charging) return "";
  const start = unit.chargingStartedAt?.toDate
    ? unit.chargingStartedAt.toDate()
    : null;
  if (!start) return "";
  const ms = Date.now() - start.getTime();
  const min = Math.floor(ms / 60000);
  const hr  = Math.floor(min / 60);
  return hr > 0 ? `${hr}h ${min % 60}m` : `${min}m`;
}

// Days since the last check. Returns null if the unit has never been checked.
export function daysSinceCheck(unit) {
  const last = unit?.lastCheckAt?.toDate ? unit.lastCheckAt.toDate() : null;
  if (!last) return null;
  return Math.floor((Date.now() - last.getTime()) / 86400000);
}

// True when a unit needs a battery check now — overdue OR due. Used by
// the manager headline + KPI tile so a freshly-imported lot (everything
// "new") doesn't read as "0 needs charged".
export function needsCharge(unit) {
  const s = chargeCycleStatus(unit);
  return s === "overdue" || s === "due";
}

// Whether the last check is stale by the charge-cycle's overdue threshold
// (35 days). Kept around because callers exist; previously hardcoded to
// 7d which didn't match the legacy app. Now thresholdDays defaults to 35.
export function isCheckStale(unit, thresholdDays) {
  const days = daysSinceCheck(unit);
  if (days == null) return false;
  const t = typeof thresholdDays === "number" ? thresholdDays : _activeCycle.overdueDays;
  return days > t;
}

// Risk score for the manager view. Higher = more attention needed.
// Mirrors the legacy app's aiRiskScore weighting (lines 2683–2696):
//   voltage     < 12.0  → +20
//   voltage     < 12.2  → +12
//   voltage     < 12.4  → +5
//   ds-check    > 60    → +45
//   ds-check    > 45    → +35
//   ds-check    > 35    → +22
export function riskScore(unit) {
  let s = 0;
  const v = unit?.voltage;
  if (typeof v === "number") {
    if      (v < 12.0) s += 20;
    else if (v < 12.2) s += 12;
    else if (v < 12.4) s += 5;
  }
  const days = daysSinceCheck(unit);
  if (days != null) {
    if      (days > 60) s += 45;
    else if (days > 45) s += 35;
    else if (days > 35) s += 22;
  } else if ((unit?.daysOnLot ?? 0) > CHARGE_CYCLE.overdueDays) {
    // Never checked AND past the overdue window — treat as severely stale.
    s += 22;
  }
  return s;
}

// Format a Firestore Timestamp / Date / null as relative time.
export function relativeTime(ts) {
  const d = ts?.toDate ? ts.toDate() : (ts instanceof Date ? ts : null);
  if (!d) return "—";
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diff < 60)        return "just now";
  if (diff < 3600)      return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400)     return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
}

// Bucket an array of units into critical / warning / ok / hv / unknown groups
// preserving the existing order within each bucket.
export function bucketByUrgency(units) {
  const buckets = { critical: [], warning: [], hv: [], ok: [], unknown: [] };
  for (const u of units) buckets[voltageState(u)].push(u);
  return buckets;
}
